import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconKey,
  IconLanguage,
  IconLogout,
  IconMoon,
  IconSettings,
  IconSun,
  IconUser,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router";
import { useModule, usePendingDocuments } from "../api/hooks";
import { useAuth } from "../auth/auth";
import { PasswordModal } from "../auth/password-modal";
import { LOCALE_LABEL, LOCALES, setLocale, type UiLocale } from "../i18n";

export function Shell() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [opened, { toggle, close }] = useDisclosure(false);
  const [passwordOpen, passwordModal] = useDisclosure(false);
  const { setColorScheme } = useMantineColorScheme();
  const computedScheme = useComputedColorScheme("light");
  const pending = usePendingDocuments(Boolean(user));
  const pendingCount = pending.data?.count ?? 0;
  const uploadOn = useModule("upload");

  const links = [
    { to: "/", label: t("nav.search"), end: true },
    { to: "/documents", label: t("nav.documents") },
    ...(uploadOn ? [{ to: "/upload", label: t("nav.upload") }] : []),
    ...(user?.role === "admin" ? [{ to: "/admin", label: t("nav.admin") }] : []),
  ];

  const nav = links.map((l) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.end ?? false}
      onClick={close}
      style={({ isActive }) => ({
        textDecoration: "none",
        color: isActive ? "var(--mantine-color-indigo-6)" : "inherit",
        fontWeight: isActive ? 600 : 400,
        padding: "6px 10px",
        borderRadius: 6,
      })}
    >
      {l.label}
    </NavLink>
  ));

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 220, breakpoint: "sm", collapsed: { desktop: true, mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            {/* Title text only where it fits next to the links; on phones it is the only branding. */}
            <UnstyledButton onClick={() => navigate("/")} style={{ minWidth: 0 }}>
              <Text fw={600} size="lg" truncate="end" hiddenFrom="sm">
                {t("app.shortTitle")}
              </Text>
              <Text fw={600} size="lg" style={{ whiteSpace: "nowrap" }} visibleFrom="lg">
                {t("app.shortTitle")}
              </Text>
            </UnstyledButton>
            <Group gap={0} visibleFrom="sm" wrap="nowrap" style={{ whiteSpace: "nowrap" }}>
              {nav}
            </Group>
          </Group>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {pendingCount > 0 && (
              <Tooltip
                label={pending.data?.items
                  .slice(0, 5)
                  .map((d) => `${d.title} · ${t(`status.${d.status}`, d.status)}`)
                  .join("\n")}
                multiline
                maw={360}
              >
                <Badge
                  component={NavLink}
                  to="/upload"
                  variant="light"
                  color="indigo"
                  size="lg"
                  leftSection={<Loader size={10} color="indigo" />}
                  style={{ cursor: "pointer", textTransform: "none" }}
                >
                  <Text span visibleFrom="sm" inherit>
                    {t("nav.indexing", { count: pendingCount })}
                  </Text>
                  <Text span hiddenFrom="sm" inherit>
                    {pendingCount}
                  </Text>
                </Badge>
              </Tooltip>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={t("nav.themeToggle")}
              onClick={() => setColorScheme(computedScheme === "dark" ? "light" : "dark")}
            >
              {computedScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
            <Menu shadow="md" width={160}>
              <Menu.Target>
                <Button variant="subtle" size="compact-sm" leftSection={<IconLanguage size={16} />}>
                  <Text span visibleFrom="lg" inherit>
                    {LOCALE_LABEL[(i18n.language as UiLocale) ?? "hy"] ?? "Հայերեն"}
                  </Text>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {LOCALES.map((l) => (
                  <Menu.Item
                    key={l}
                    onClick={() => setLocale(l)}
                    fw={l === i18n.language ? 600 : 400}
                  >
                    {LOCALE_LABEL[l]}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            {user && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    leftSection={<IconUser size={16} />}
                    rightSection={<IconChevronDown size={14} />}
                  >
                    <Text span visibleFrom="md" inherit>
                      {user.displayName}
                    </Text>
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{t(`auth.role.${user.role}`)}</Menu.Label>
                  <Menu.Item
                    leftSection={<IconSettings size={16} />}
                    onClick={() => navigate("/settings")}
                  >
                    {t("settings.menu")}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconKey size={16} />} onClick={passwordModal.open}>
                    {t("password.menu")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconLogout size={16} />}
                    onClick={async () => {
                      await logout();
                      navigate("/login");
                    }}
                  >
                    {t("auth.logout")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="md">{nav}</AppShell.Navbar>
      <PasswordModal opened={passwordOpen} onClose={passwordModal.close} />
      <AppShell.Main>
        <Container size="xl" px={0}>
          <Outlet />
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
